/**
 * Turn-text helpers moved verbatim from the retired ConversationTranscript.tsx (2026-11-02):
 * the highlight lists `HighlightedResponse` consumes, and the drop-out reason sentence.
 * Both bodies are unchanged — the pair gate below is a targets.md §11.1 invariant.
 */

import type {
  ConversationTurn,
  ConversationTurnMatch,
  TargetHighlight,
  TargetSourceHighlight,
} from '@traaker/shared';

/**
 * A turn's `targetMatches` → the two highlight lists `HighlightedResponse` consumes.
 *
 * A `pair` match becomes a CONDITIONAL highlight, exactly as Brand Perception's
 * `targets_shadow._highlights` builds one: the row stores the halves glued as `'a|b'`,
 * so split them and ship half `a` as `value` with half `b` as `pairWith` + the
 * matcher's `window`. `targetTermsFrom` then re-runs `isPairSatisfied` per occurrence.
 * Half `b` gets no entry of its own — it is a marque alias by construction, already
 * painted by the marque's own signal in the same colour (D45).
 *
 * A pair with no `window` is DROPPED (rows written before 2026-09-25): the gate cannot
 * be evaluated, and an UNGATED pair half is exactly what once painted a competitor's
 * address in the client's brand colour (targets.md §11.1). Failing closed loses a
 * highlight; the alternative invents one. The match itself is untouched either way, so
 * the turn still reads as cited.
 */
export function highlightsOf(matches: ConversationTurnMatch[] | undefined): {
  prose: TargetHighlight[];
  sources: TargetSourceHighlight[];
} {
  const prose: TargetHighlight[] = [];
  const sources: TargetSourceHighlight[] = [];
  for (const m of matches ?? []) {
    if (m.via === 'source') {
      if (m.sourceIndex !== undefined) {
        sources.push({ sourceIndex: m.sourceIndex, targetId: m.targetId, rootMarqueId: m.rootMarqueId });
      }
      continue;
    }
    if (m.kind === 'pair') {
      // Split from the RIGHT: half `b` is a marque alias and never contains '|',
      // while half `a` is free text that could.
      const cut = m.value.lastIndexOf('|');
      if (cut <= 0) continue;
      const halfA = m.value.slice(0, cut);
      const halfB = m.value.slice(cut + 1);
      if (!halfA || !halfB || !m.window || m.window <= 0) continue;
      prose.push({
        value: halfA,
        pairWith: halfB,
        window: m.window,
        targetId: m.targetId,
        rootMarqueId: m.rootMarqueId,
      });
      continue;
    }
    prose.push({ value: m.value, targetId: m.targetId, rootMarqueId: m.rootMarqueId });
  }
  return { prose, sources };
}

/**
 * The drop-out band's one sentence, derived from signals already measured on the turn
 * (there is no drop-reason field). Most specific first; the last rung says nothing
 * happened that we can see, rather than inventing a cause.
 */
export function dropReason(
  turn: ConversationTurn,
  prev: ConversationTurn | undefined,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  if (turn.eliminated === true) return t('conversations.annoDropWhyEliminated');

  // Tracked competitors first (Layer A, deterministic); the judge's brand list is the
  // fallback so a rival we do not track yet still names itself as the reason.
  const rivals = ((turn.competitorsMentioned ?? []).length > 0
    ? turn.competitorsMentioned
    : (turn.otherBrandsCited ?? [])
  ).filter(Boolean);
  if (rivals.length > 0) {
    // Two names, no "+N" — the « N concurrents cités » row on the same rail already
    // carries the count, and a second count here would just contradict it on rounding.
    return t('conversations.annoDropWhyCompetitors', { brands: rivals.slice(0, 2).join(', ') });
  }

  if (prev?.brandIsSource && !turn.brandIsSource && (turn.sources?.length ?? 0) > 0) {
    return t('conversations.annoDropWhyLostSource');
  }
  if ((turn.sources?.length ?? 0) === 0 && (prev?.sources?.length ?? 0) > 0) {
    return t('conversations.annoDropWhyNoSources');
  }
  return t('conversations.annoDropWhyUnknown');
}
